import { useMemo, useState } from 'react';

import { Users } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { ESTADO_DO_CADASTRO } from '@/modulos/portal-credenciado/comum/recursos';
import { ErroApi } from '@/nucleo/auth/cliente-api';
import { CopyableCpfCnpj } from '@/ui/copyable';
import { DataTable } from '@/ui/DataTable';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { PageHeader } from '@/ui/PageHeader';
import { StatusBadge } from '@/ui/StatusBadge';
import { Badge } from '@/ui/base/badge';
import type { TableViewColumn } from '@/ui/table-view/tabelaVisoes';
import { useTableViewPreferences } from '@/ui/table-view/useTableViewPreferences';

import { useMonitores } from './dados';
import type { FiltroDeMonitores, Monitor } from './contrato';

const TAMANHO_DA_PAGINA = 10;

/**
 * Módulo, e não dentro da tela: `useTableViewPreferences` pede lista estável.
 * O nome identifica a linha e não se oculta.
 */
const COLUNAS: TableViewColumn<Monitor>[] = [
  {
    id: 'nome',
    header: 'Nome',
    // O nome é o Link para o detalhe: o botão de copiar o CPF é um controle
    // focável dentro da linha, então a linha não pode ser também um botão
    // (`nested-interactive`, regras de interface 5 e 9). O teclado abre o
    // detalhe pelo Link; o mouse continua abrindo pela linha inteira. Mesmo
    // desenho de Solicitações.
    cell: (monitor) => (
      <Link
        to={`/credenciado/monitores/${monitor.id}`}
        className="focus-visible:ring-ring rounded hover:underline focus:outline-hidden focus-visible:ring-2"
      >
        {monitor.nome}
      </Link>
    ),
    sortKey: 'nome',
    isCritical: true,
  },
  {
    id: 'cpf',
    header: 'CPF',
    cell: (monitor) => <CopyableCpfCnpj value={monitor.cpf} className="whitespace-nowrap" />,
  },
  {
    id: 'cursos',
    header: 'Cursos',
    cell: (monitor) =>
      monitor.curso_primeiro_socorros === null && monitor.curso_transporte_escolar === null ? (
        <span className="text-sm text-muted-foreground">—</span>
      ) : (
        <div className="flex flex-wrap gap-1">
          {monitor.curso_primeiro_socorros && (
            <Badge variant="outline" className="text-[10px]">
              1ºs Socorros
            </Badge>
          )}
          {monitor.curso_transporte_escolar && (
            <Badge variant="outline" className="text-[10px]">
              Transp. Escolar
            </Badge>
          )}
        </div>
      ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: (monitor) => (
      <StatusBadge
        status={ESTADO_DO_CADASTRO[monitor.situacao].selo}
        label={ESTADO_DO_CADASTRO[monitor.situacao].rotulo}
      />
    ),
    sortKey: 'situacao',
    isStatusColumn: true,
  },
];

/**
 * `/credenciado/monitores` — "Meus Monitores", com a composição de
 * `pages/portal/PortalMonitores.tsx` do protótipo: mesmo cabeçalho, um único
 * filtro (Buscar, por nome ou CPF) e as quatro colunas Nome, CPF, Cursos e
 * Status, em ordem de nome.
 *
 * O dado vem da API (`comum/recursos.ts`): nome, CPF, estado do cadastro e
 * documentos. A API ainda não devolve os cursos, e a coluna mostra "—";
 * "Status" é o estado do cadastro (em análise, aprovado, reprovado).
 *
 * Sem escrita: é uma tela de consulta. Vale para qualquer tipo de credenciado
 * (PF, PJ ou Cooperativa), como no protótipo, que não restringe a rota.
 *
 * Uma diferença deliberada do protótipo: o estado vazio de filtro diz que o
 * filtro não achou ninguém. O protótipo repete "Nenhum monitor cadastrado"
 * mesmo com monitores na lista, o que afirma um fato falso sobre o cadastro.
 *
 * `embutida`: a tela entra como aba de Frota & Equipe, que já tem o `h1` da
 * página; o título desta vira `h2` (`PageHeader nivel={2}`). A rota própria
 * não passa a prop e continua com o `h1`.
 */
export function MonitoresPage({ embutida = false }: { embutida?: boolean }) {
  const navigate = useNavigate();

  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [tamanho, setTamanho] = useState(TAMANHO_DA_PAGINA);

  const filtro = useMemo<FiltroDeMonitores>(() => ({ busca: busca || undefined }), [busca]);

  const { data, error, isLoading, isError, refetch } = useMonitores(filtro, pagina, tamanho);

  const campos: FilterFieldConfig[] = [
    {
      key: 'busca',
      label: 'Buscar',
      type: 'search',
      placeholder: 'Buscar por nome ou CPF...',
      ariaLabel: 'Buscar monitores por nome ou CPF',
      flex: true,
    },
  ];

  const tableView = useTableViewPreferences({
    contextKey: 'credenciado:monitores:list',
    columns: COLUNAS,
  });

  return (
    <div className="space-y-4">
      <PageHeader
        nivel={embutida ? 2 : 1}
        title="Meus Monitores"
        description="Monitores vinculados ao seu credenciamento"
      />

      <FilterBar
        fields={campos}
        values={{ busca }}
        onChange={(chave, valor) => {
          if (chave === 'busca') setBusca(valor);
          setPagina(1);
        }}
        onClear={() => {
          setBusca('');
          setPagina(1);
        }}
      />

      {error instanceof ErroApi && error.pedeOutroCaminho ? (
        // 403: perfil sem acesso ou cadastro sem vínculo. Tentar de novo não resolve.
        <ErrorState variant="forbidden" description={error.message} />
      ) : isError ? (
        <ErrorState
          variant="load-failed"
          description="Não foi possível carregar seus monitores agora. Tente novamente em instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      ) : (
        <DataTable
          columns={tableView.orderedColumns}
          personalizacao={tableView}
          data={data?.itens ?? []}
          isLoading={isLoading}
          total={data?.total}
          page={pagina}
          perPage={tamanho}
          onPageChange={setPagina}
          onPerPageChange={(novoTamanho) => {
            setTamanho(novoTamanho);
            setPagina(1);
          }}
          onRowClick={(monitor) => navigate(`/credenciado/monitores/${monitor.id}`)}
          linhaComElementoInterativo
          emptyIcon={Users}
          emptyTitle={busca ? 'Nenhum monitor encontrado' : 'Nenhum monitor cadastrado'}
          emptyDescription={
            busca
              ? 'Nenhum monitor atende a essa busca. Tente outro nome ou CPF.'
              : 'Você ainda não possui monitores vinculados.'
          }
          minWidth="600px"
          ariaLabel="Lista de monitores"
        />
      )}
    </div>
  );
}

export default MonitoresPage;
